/**
 * @fileoverview The Weather page's week ahead: three numbers against their normals, a
 * sentence for the week, and a row a day with its low and high, each beside a circle
 * whose colour slides with how far it sits from normal: cream at normal, blue
 * 10° or more below, terracotta 10° or more above, blended in between.
 *
 * What the week means is the server's (the Today brief's weatherWeek,
 * services/today/weatherWeek; audit batch 4, A24): each reading's tone against
 * its normal (near normal within the Grow setting weather.NEAR_NORMAL_F), the
 * week's sentence (the day weather.NOTABLE_F off named; the wet days by
 * Today's own rule, a rain day or a 50% chance, so Weather and Today never
 * disagree), whether a day's chance of rain is worth showing on its row
 * (weather.RAIN_SHOWN_PCT), and each day's sky: its words in the reader's
 * language and its icon key (skyText, skyIcon; W24 Job D: the app matches no
 * forecast words). Before the brief is in the rows draw no sky, and
 * the tones are plain and there is no sentence. Rain is in inches, or
 * millimetres for a gardener reading °C (formatRain).
 *
 * Where: the Weather page (/weather), under the garden's week, inside
 * #forecast; only when the plant outlook has arrived.
 * API: none of its own: the week's readings are the plant outlook's
 * conditions (GET /plants/outlook), what they mean the Today brief's
 * (GET /today/brief, weatherWeek).
 * Settings: the gardener's temperature unit (conditions.tempUnit).
 * Colours: the --temp-warm, --temp-normal, --temp-cool and --temp-edge
 * tokens of each theme (src/style.css), blended with CSS color-mix in oklab;
 * no fixed colour here. The scale is 10°F, or that converted for a gardener
 * reading °C (5.6°C in the colours; the legend says it rounded, 6°).
 * Words (en/es): weather.next7, weather.week.* (days, nights, rain, low, high,
 * normalIs, nextSeven, today, normalFor, rainExpected, keyNormal,
 * keyWarmer, keyCooler, offBy, scale); the sentence is the brief's.
 *
 * @module ForecastWeek
 */

import React from 'react';
import { ChevronDown, Droplets } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { SkyIcon } from '@/shared/components/SkyIcon';
import { formatRain } from '@/shared/utils/rainUtils';
import type { ConditionsDay, OutlookConditions } from '@/shared/api/outlook';
import type { WeatherWeek, WeatherWeekDay } from '@/shared/api/todayBrief';
import { DateTime } from '@/shared/utils/dateUtils';

/** The week the page shows. */
interface ForecastWeekProps {
  /** From the plant outlook, already in the gardener's unit. */
  conditions: OutlookConditions;
  /** What it means, the Today brief's (weatherWeek); null before it is in. */
  said: WeatherWeek | null;
}

/** How a day's end reads against its normal: warmer, cooler, or near enough (the server's). */
type Tone = WeatherWeekDay['minTone'];

/** How many letters of a day's name the rows show ("Tue"): the row's own width. */
const DAY_NAME_CHARS = 3;

/** How far from normal is the full cool or full warm colour, in °F. */
const SCALE_SPAN_F = 10;

/** The same span in °C: 10°F converted (5.56), so it means the same as 10°F. */
const SCALE_SPAN_C = SCALE_SPAN_F * 5 / 9;

/**
 * The scale's reach in the gardener's unit.
 *
 * @param unit - The gardener's temperature unit.
 * @returns 10°F converted for °C (5.56), 10 for °F.
 */
function scaleSpan(unit: 'f' | 'c' | null | undefined): number {
  return unit === 'c' ? SCALE_SPAN_C : SCALE_SPAN_F;
}

/**
 * A CSS colour for how far a reading is from its normal: the theme's normal
 * (cream) at 0, sliding in proportion to its cool blue or warm terracotta,
 * fully so at the span or beyond (color-mix in oklab).
 *
 * @param variance - Departure from normal, in the gardener's unit, or null.
 * @param span - The departure that is the full colour.
 * @returns A color-mix() expression; plain normal when there is no normal.
 */
function scaleColour(variance: number | null, span: number): string {
  const normal = 'hsl(var(--temp-normal))';
  if (variance === null) return normal;
  const share = Math.min(Math.abs(variance) / span, 1);
  const end = variance > 0 ? 'hsl(var(--temp-warm))' : 'hsl(var(--temp-cool))';
  return `color-mix(in oklab, ${end} ${Math.round(share * 100)}%, ${normal})`;
}

/** The thin outline every circle and the legend bar wear, from the theme's edge token. */
const EDGE = '1px solid hsl(var(--temp-edge) / 0.55)';

/** The legend bar: cool, cream, warm, in the same oklab blend as the circles. */
const LEGEND_BAR =
  'linear-gradient(to right in oklab, hsl(var(--temp-cool)), hsl(var(--temp-normal)), hsl(var(--temp-warm)))';

/** Each tone's word (read aloud beside a circle). */
const TONE_KEY: Record<Tone, string> = {
  warm: 'weather.week.keyWarmer',
  cool: 'weather.week.keyCooler',
  plain: 'weather.week.keyNormal',
};

/** The row's columns: day, sky, low, high, fold arrow. */
const ROW_GRID = 'grid grid-cols-[4.25rem_1.5rem_minmax(0,1fr)_minmax(0,1fr)_1rem] items-center gap-1.5';

/**
 * A reading beside its circle.
 *
 * @param props.colour - The circle's colour (scaleColour).
 * @param props.text - The reading, already formatted.
 * @param props.strong - Bold, for the high.
 * @param props.label - Cooler, normal or warmer, and by how much, for screen readers.
 */
const Reading: React.FC<{ colour: string; text: string; strong?: boolean; label: string }> = ({ colour, text, strong, label }) => (
  <span className="flex items-center gap-2">
    <span className="h-3.5 w-3.5 shrink-0 rounded-full" style={{ background: colour, border: EDGE }} aria-hidden="true" />
    <span className={cn('text-sm tabular-nums', strong ? 'font-semibold' : 'text-muted-foreground')}>{text}</span>
    <span className="sr-only">{label}</span>
  </span>
);

/**
 * The week ahead against its thirty-year normals.
 *
 * Each day was a column of eight readings: high, low, the usual pair, the gap,
 * the chance of rain, the amount, and the forecast's words. Nothing lined up
 * down the week, and the week itself was said three times over, in two
 * sentences, four tiles and the columns.
 *
 * Now the week is three numbers against their normals and one sentence, and
 * each day is a row: its low and its high, each beside one circle that slides
 * from blue (10° or more cooler than normal) through cream (normal) to
 * terracotta (10° or more warmer), so an unusual day is seen before a word of it is read. The
 * forecast's words and the normal numbers are one tap away. It used to draw
 * both ends as dots on a line with ticks for the normals.
 *
 * @param props.conditions - The outlook's week, in the gardener's unit.
 * @param props.said - What it means, the Today brief's; null before it is in.
 */
export const ForecastWeek: React.FC<ForecastWeekProps> = ({ conditions: given, said }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  // The days named in the reader's language: the pipeline names them in English ("Fri" in a Spanish week).
  const conditions = React.useMemo<OutlookConditions>(
    () => ({ ...given, days: given.days.map((day) => ({ ...day, label: weekdayName(day, language) })) }),
    [given, language],
  );
  const { days, metrics } = conditions;
  const [open, setOpen] = React.useState<string | null>(null);
  /** Each day as the server judged it, by date. */
  const judged = React.useMemo(() => new Map((said?.days ?? []).map((day) => [day.date, day])), [said]);

  const span = scaleSpan(conditions.tempUnit);
  /**
   * A reading's screen-reader words: cooler, normal or warmer (the server's tone), and the degrees off.
   *
   * @param variance - Departure from normal, or null.
   * @param tone - The server's tone for it; plain before the brief is in.
   */
  const spoken = (variance: number | null, tone: Tone): string => {
    const word = t(TONE_KEY[tone]);
    return variance === null || tone === 'plain'
      ? word
      : t('weather.week.offBy', { word, degrees: Math.round(Math.abs(variance)) });
  };

  /** A reading rounded, or a dash for none. */
  const deg = (value: number | null): string => (value === null ? '–' : `${Math.round(value)}°`);
  /** Rain from the outlook's inches in the gardener's unit, "0.4″" or "10 mm" (formatRain). */
  const rain = (inches: number): string => formatRain(inches, conditions.tempUnit);
  /** "Today" for the first day, the day's short name after it. */
  const dayName = (day: ConditionsDay, index: number) =>
    index === 0 ? t('weather.week.today') : (day.label ?? '').slice(0, DAY_NAME_CHARS);

  const sentence = said?.sentence ?? null;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h2 className="mb-3 text-base font-semibold font-body">{t('weather.next7')}</h2>

      {/* The week against normal, in three numbers. */}
      <dl className="mb-3 grid grid-cols-3 gap-2 border-b pb-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('weather.week.days')}</dt>
          <dd className="text-xl font-bold font-body">{deg(metrics.avgDaytime)}</dd>
          <dd className="text-xs text-muted-foreground">{t('weather.week.normalIs', { value: deg(metrics.avgNormalMax) })}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('weather.week.nights')}</dt>
          <dd className="text-xl font-bold font-body">{deg(metrics.avgNight)}</dd>
          <dd className="text-xs text-muted-foreground">{t('weather.week.normalIs', { value: deg(metrics.avgNormalMin) })}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('weather.week.rain')}</dt>
          <dd className="text-xl font-bold font-body">
            {metrics.totalPrecip7d === null ? '–' : rain(metrics.totalPrecip7d)}
          </dd>
          <dd className="text-xs text-muted-foreground">{t('weather.week.nextSeven')}</dd>
        </div>
      </dl>

      {sentence && <p className="mb-2 text-sm">{sentence}</p>}

      {/* Column labels, on the rows' own grid. */}
      <div className={cn(ROW_GRID, 'border-b pb-1 text-[11px] uppercase tracking-wide text-muted-foreground')} aria-hidden="true">
        <span /><span />
        <span>{t('weather.week.low')}</span>
        <span>{t('weather.week.high')}</span>
        <span />
      </div>

      <ul>
        {days.map((day, index) => {
          const isOpen = open === day.date;
          const chance = day.pop ?? 0;
          const judgedDay = judged.get(day.date);
          /** The chance is shown where the server says it is worth it (weather.RAIN_SHOWN_PCT). */
          const chanceShown = Boolean(judgedDay?.chanceShown);
          /** The day's rain, in inches (shown in the gardener's unit). */
          const amount = day.precipIn ?? 0;
          /** The day's sky, the server's: its words in the reader's language and its icon key. */
          const words = judgedDay?.skyText ?? '';
          return (
            <li key={day.date} className="border-t first:border-t-0">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : day.date)}
                aria-expanded={isOpen}
                className={cn(ROW_GRID, 'min-h-11 w-full rounded-sm py-1 text-left transition-colors hover:bg-muted/50')}
              >
                {/* The rain rides under the day's name, so the bar keeps the
                    width: the chance and how much. The amount was only in the
                    opened day, so a wet weekend's rows showed no rain at all. */}
                <span className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold">{dayName(day, index)}</span>
                  {(chanceShown || amount > 0) && (
                    <span className="flex items-center gap-0.5 whitespace-nowrap text-[11px] text-verdict-water tabular-nums">
                      <Droplets className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                      {chanceShown && `${chance}%`}
                      {chanceShown && amount > 0 && ' · '}
                      {amount > 0 && rain(amount)}
                    </span>
                  )}
                </span>
                {/* The day's sky key, the server's, in the app's standard icons (SkyIcon); the slot holds its place before the brief. */}
                <span className="flex h-6 w-6">
                  <SkyIcon sky={judgedDay?.skyIcon} className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
                </span>
                <Reading colour={scaleColour(day.minVariance, span)} text={deg(day.min)} label={spoken(day.minVariance, judgedDay?.minTone ?? 'plain')} />
                <Reading strong colour={scaleColour(day.maxVariance, span)} text={deg(day.max)} label={spoken(day.maxVariance, judgedDay?.maxTone ?? 'plain')} />
                <ChevronDown
                  className={cn('h-4 w-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
              {isOpen && (
                <p className="pb-2.5 pl-[4.4rem] text-sm text-muted-foreground">
                  {words ? `${words}. ` : ''}
                  {t('weather.week.normalFor', { low: deg(day.normalMin), high: deg(day.normalMax) })}
                  {(day.precipIn ?? 0) > 0 ? ` ${t('weather.week.rainExpected', { amount: rain(day.precipIn ?? 0) })}` : ''}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {/* The scale: one bar from full cool to full warm, labelled in the gardener's unit. */}
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
        <div className="h-2.5 rounded-full" style={{ background: LEGEND_BAR, border: EDGE }} aria-hidden="true" />
        <p className="mt-1 text-center">{t('weather.week.scale', { degrees: Math.round(span) })}</p>
      </div>
    </section>
  );
};

/**
 * A forecast day's weekday in the app's language, "Friday" or "viernes",
 * from its date; the pipeline's English label when the date cannot be read.
 *
 * @param day - The day.
 * @param language - The app's language.
 * @returns The weekday's full name, or the label as given.
 */
function weekdayName(day: ConditionsDay, language: string): string | null {
  const at = DateTime.fromISO(day.date);
  return at.isValid ? at.setLocale(language).toFormat('cccc') : day.label;
}
